<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { format, parseISO } from 'date-fns';

export default {
  data() {
    return {
      selectedColumns: [],
      initialColumns: [],
      isSaving: false,
      isRegenerating: false,
      fetchError: null,
    };
  },

  computed: {
    ...mapGetters('gsheet', ['embeddingConfig', 'isLoading', 'isError']),

    kbId() {
      return this.$route.params.GSheetKBId;
    },

    availableColumns() {
      return this.embeddingConfig?.available_columns || [];
    },

    selectedColumnsFromConfig() {
      return this.embeddingConfig?.selected_columns || [];
    },

    lastConfigUpdate() {
      if (!this.embeddingConfig?.last_config_update) return null;

      const date = parseISO(this.embeddingConfig.last_config_update);

      return format(date, 'yyyy-MM-dd, hh:mm a');
    },

    staleColumnsWarning() {
      return this.embeddingConfig?.stale_columns_warning || null;
    },

    totalRows() {
      return this.embeddingConfig?.total_rows ?? null;
    },

    rowsWithEmbeddings() {
      return this.embeddingConfig?.rows_with_embeddings ?? null;
    },

    hasChanges() {
      return JSON.stringify(this.selectedColumns.sort()) !==
             JSON.stringify(this.initialColumns.sort());
    },

    // Derived from Vuex state - more reliable
    configData() {
      return this.embeddingConfig || {};
    }
  },

  watch: {
    // Watch for config changes and update local state
    selectedColumnsFromConfig: {
      handler(newColumns) {
        this.selectedColumns = [...newColumns];
        this.initialColumns = [...newColumns];
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('gsheet', [
      'getEmbeddingConfig',
      'updateEmbeddingConfig',
      'regenerateEmbeddings'
    ]),

    async fetchData() {
      this.fetchError = null;
      try {
        await this.getEmbeddingConfig({ kb_id: this.kbId });
        // Local state will be updated via the watcher
      } catch (error) {
        this.fetchError = 'Failed to load configuration';
        console.error('Failed to fetch embedding config:', error);
      }
    },

    onToggleColumn(colName) {
      if (this.selectedColumns.includes(colName)) {
        this.selectedColumns = this.selectedColumns.filter(c => c !== colName);
      } else {
        this.selectedColumns.push(colName);
      }
    },

    async saveConfig() {
      this.isSaving = true;

      try {
        await this.updateEmbeddingConfig({
          kb_id: this.kbId,
          payload: { columns: this.selectedColumns }
        });
        
        this.initialColumns = [...this.selectedColumns];
        useAlert('Config saved successfully');
        await this.fetchData();
      } catch (error) {
        console.error('Failed to save config:', error);
        useAlert(`Failed to save config: ${error}`);
      } finally {
        this.isSaving = false;
      }
    },

    async triggerRegenerate() {
      if (!confirm("This will regenerate embeddings for ALL rows. Continue?")) return;

      this.isRegenerating = true;

      try {
        await this.regenerateEmbeddings({
          kb_id: this.kbId,
        });
        useAlert('Embeddings regenerated successfully');
        await this.fetchData();
      } catch (error) {
        console.error('Failed to regenerate embeddings:', error);
        useAlert(`Failed to regenerate embeddings: ${error}`);
      } finally {
        this.isRegenerating = false;
      }
    }
  },

  mounted() {
    this.fetchData();
  }
};
</script>

<template>
  <div class="bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 rounded-md p-4">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold dark:text-white">Embedding Configuration</h2>

      <button
        class="px-3 py-1.5 text-sm rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition"
        @click="fetchData"
      >
        Refresh
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-slate-500 dark:text-slate-300 text-sm py-2">
      Loading embedding configuration...
    </div>

    <!-- Error State -->
    <div v-if="isError" class="text-red-500 text-sm py-2">
      Failed to load config. Please try again.
    </div>

    <div v-if="!isLoading">

      <!-- Stats Block -->
      <div class="mb-6">
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-1">
          Rows with embeddings: {{ rowsWithEmbeddings }} / {{ totalRows }}
        </p>

        <p v-if="lastConfigUpdate" class="text-xs text-slate-400 dark:text-slate-500">
          Last updated: {{ lastConfigUpdate }}
        </p>

        <p v-if="staleColumnsWarning" class="text-xs text-yellow-600 dark:text-yellow-400">
          ⚠️ {{ staleColumnsWarning }}
        </p>
      </div>

      <!-- Column Selection -->
      <div class="mb-6">
        <h3 class="font-medium text-slate-700 dark:text-slate-200 mb-2">
          Select Columns for Embeddings
        </h3>

        <div class="space-y-2">
          <div
            v-for="col in availableColumns"
            :key="col.column_name"
            class="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-slate-700 cursor-pointer 
                  hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            @click="onToggleColumn(col.column_name)"
          >
            <div>
              <p class="font-medium dark:text-white">
                {{ col.column_name }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ col.detected_type }} | enabled: {{ col.is_enabled ? 'yes' : 'no' }}
              </p>
            </div>

            <input
              type="checkbox"
              class="form-checkbox h-4 w-4 pointer-events-none" 
              :disabled="!col.is_enabled"
              :checked="selectedColumns.includes(col.column_name)"
            />
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-between">
        <!-- Regenerate Button -->
        <button
          class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
          :disabled="isRegenerating"
          @click="triggerRegenerate"
        >
          <span v-if="isRegenerating">Regenerating...</span>
          <span v-else>Regenerate Embeddings</span>
        </button>

        <button
          class="px-4 py-2 bg-woot-600 text-white rounded hover:bg-woot-700 disabled:opacity-50 disabled:pointer-events-none flex items-center gap-2"
          :disabled="!hasChanges || isSaving"
          @click="saveConfig"
        >
          <span v-if="isSaving">Saving...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </div>
  </div>
</template>